import { Box, DialogContent, DialogTitle, Drawer, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Trash2, CircleCheck, ImagePlus, X } from 'lucide-react';

import React, { useEffect, useRef } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { creatureVariants, extractImageSrcFromDrop, fileToDataUrl, measureImage } from '../../utils/util';


export function CreatureDialog({ creature, open, onClose, onChange, onDelete }) {
    const [item, setItem] = React.useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
    const fileInputRef = useRef(null);


    useEffect(() => {
        console.log("Setting creature in dialog", creature)
        setItem({ ...creature })
    }, [creature])

    function doUpdate(newValues) {
        const updated = { ...item, ...newValues }
        onChange(updated);
        setItem(updated);
    }

    function addImage(src) {
        if (!src) return;
        const extras = Array.isArray(item.images) ? item.images : [];
        const has = s => s === item.src || extras.some(v => (v.src || v) === s);
        if (has(src)) return; // no duplicates
        // Capture the aspect ratio so the group can size to the tallest variant.
        measureImage(src)
            .then(variant => doUpdate({ images: [...extras, variant] }))
            .catch(() => {});
    }

    function removeImage(src) {
        const extras = Array.isArray(item.images) ? item.images : [];
        doUpdate({ images: extras.filter(v => (v.src || v) !== src) });
    }

    function onGalleryDrop(e) {
        e.preventDefault();
        extractImageSrcFromDrop(e.dataTransfer).then(addImage);
    }

    function onFilePicked(e) {
        const file = e.target.files && e.target.files[0];
        if (file) fileToDataUrl(file).then(addImage);
        e.target.value = ''; // allow re-picking the same file
    }

    if (item == null) {
        return "";
    }

    const variants = creatureVariants(item);

    return (<Drawer anchor='right' open={open} onClose={onClose}>
        <DialogTitle>Edit creature</DialogTitle>
        <DialogContent>
            <Stack direction="column" spacing={2}>
                <img src={item.src} alt={item.name} loading="lazy" style={{ maxWidth: "300px" }} />
                <TextField label="Name" value={item.name} onChange={e => { doUpdate({ name: e.target.value }) }} />
                <Select label="Size" value={item.size ? item.size : ''} onChange={e => { doUpdate({ size: e.target.value }) }} >
                    <MenuItem value={0.5}>Tiny</MenuItem>
                    <MenuItem value={1}>Medium/Small</MenuItem>
                    <MenuItem value={2}>Large</MenuItem>
                    <MenuItem value={3}>Huge</MenuItem>
                    <MenuItem value={4}>Gargantuan</MenuItem>
                </Select>
                <Select label="Style" value={item.renderStyle || 'standee'} onChange={e => { doUpdate({ renderStyle: e.target.value }) }} >
                    <MenuItem value="standee">Standee</MenuItem>
                    <MenuItem value="rectToken">Rectangular token</MenuItem>
                    <MenuItem value="roundToken">Round token</MenuItem>
                </Select>

                <Box>
                    <Typography variant="subtitle2">Images</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Add more art and a group rotates through it for variety.
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
                        onDrop={onGalleryDrop}
                        onDragOver={e => e.preventDefault()}
                    >
                        {variants.map((variant, i) => (
                            <Box key={variant.src + i} sx={{ position: 'relative', width: 64, height: 64 }}>
                                <img src={variant.src} alt="" loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                                {i > 0 && (
                                    <Tooltip title="Remove image">
                                        <IconButton size="small" onClick={() => removeImage(variant.src)}
                                            sx={{ position: 'absolute', top: -8, right: -8, background: 'rgba(255,255,255,0.9)', '&:hover': { background: '#fff' } }}>
                                            <X size={14} strokeWidth={2} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                        <Tooltip title="Add image">
                            <IconButton onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <ImagePlus size={22} strokeWidth={2} />
                            </IconButton>
                        </Tooltip>
                        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFilePicked} />
                    </Stack>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                    <IconButton color="error"
                        onClick={e => setConfirmDeleteOpen(true)}>
                        <Trash2 size={20} strokeWidth={2} />
                    </IconButton>
                    <IconButton color="primary"
                        onClick={e => {
                            onChange(item);
                            onClose();
                            setItem(null);
                        }}>
                        <CircleCheck size={20} strokeWidth={2} />
                    </IconButton>


                </Stack>
            </Stack>

        </DialogContent>

        <ConfirmDialog
            open={confirmDeleteOpen}
            title={`Remove ${item.name} from your library?`}
            description="This can't be undone."
            confirmLabel="Remove"
            onCancel={() => setConfirmDeleteOpen(false)}
            onConfirm={() => {
                setConfirmDeleteOpen(false);
                if (onDelete && item) {
                    onDelete(creature);
                    onClose();
                    setItem(null);
                }
            }}
        />

    </Drawer>);
}
